import React, { useContext, useEffect, useRef } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AppRouteUi } from "../configs";
import { useVersionCheck } from "./hooks";
import {
  CallReturn,
  ExternalLoginPage,
  PageActivateAccount,
  PageForgotPassword,
  PageLobby,
  PageLogin,
  PageNotFound,
  PageResetPassword,
  ReturnHomeScreenCallPage,
  ChangelogPage,
} from './public';
import { MediaDiag } from './admin';
import {
  ChannelContextProvider,
  CallContextProvider,
  SocketContextProvider,
  GuestContextProvider,
  AudioDeviceContextProvider,
  AudioDeviceContext
} from "../core";
import { PageChangePassword } from "./public/ChangePasswordPage";
import { VideoGuestPage } from './call/VideoGuestPage';
import { GuestCallEndedPage } from './call/GuestCallEndedPage';
import { isElectron } from '../utils';

const FirstInteractionAudioUnlock: React.FC = () => {
  const { resumeAudioAndRegister } = useContext(AudioDeviceContext);
  const handledRef = useRef(false);

  useEffect(() => {
    if (isElectron()) return;

    const handler = () => {
      if (handledRef.current) return;
      handledRef.current = true;
      resumeAudioAndRegister();
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };

    window.addEventListener('click', handler);
    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [resumeAudioAndRegister]);

  return null;
};

export function LayoutPublic(): JSX.Element {
  useVersionCheck("login");
  const location = useLocation();
  if (
    AppRouteUi.Lobby.Root === location.pathname
  ) {
    return <Redirect to={AppRouteUi.NotFound.Root} />;
  }

  return (
    <>
      <Switch>
        <Route path={AppRouteUi.Login.Root} exact component={PageLogin} />
        <Route
          path={AppRouteUi.Login.ExternalLogin}
          exact
          component={ExternalLoginPage}
        />
        <Route
          path={AppRouteUi.ActivateAccount.Root}
          exact
          component={PageActivateAccount}
        />
        <Route
          path={AppRouteUi.ForgotPassword.Root}
          exact
          component={PageForgotPassword}
        />
        <Route
          path={AppRouteUi.ResetPassword.Root}
          exact
          component={PageResetPassword}
        />
        <Route
          path={AppRouteUi.ChangePassword.Root}
          exact
          component={PageChangePassword}
        />
        <Route
          path={AppRouteUi.Lobby.WaitingRoom.Root()}
          exact
          component={PageLobby}
        />
        <Route
          path={AppRouteUi.VideoConference.Rejoin.Root()}
          exact
          component={CallReturn}
        />
        <Route
          path={AppRouteUi.MediaDiag.Root}
          exact
          component={MediaDiag}
        />
        <Route
          path={AppRouteUi.GuestCallEnded.Root}
          exact
          component={GuestCallEndedPage}
        />
        <Route
          path={AppRouteUi.Consumer.Load()}
          exact
          component={ReturnHomeScreenCallPage}
        />
        <Route
          path={AppRouteUi.Changelog.Root}
          exact
          component={ChangelogPage}
        />
        <Route path={AppRouteUi.NotFound.Root} exact component={PageNotFound} />
        <Route
          path={`${AppRouteUi.Guest.Root}/:guid`}
          exact
          render={() => (
            <GuestContextProvider>
              <ChannelContextProvider>
                <AudioDeviceContextProvider>
                  <FirstInteractionAudioUnlock />
                  <CallContextProvider>
                    <VideoGuestPage />
                  </CallContextProvider>
                </AudioDeviceContextProvider>
              </ChannelContextProvider>
            </GuestContextProvider>
          )}
        />
      </Switch>
    </>
  );
}
